import { useState, useEffect } from 'react';
import { 
  Upload, CheckCircle, Calculator, Camera, MapPin, Phone, Mail, 
  Star, Info, Check, ChevronRight, AlertCircle, ShieldCheck
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link, useRouter } from '../components/Router';
import { trackPageView } from '../utils/analytics';
import { MultiImageUpload } from '../components/ImageUpload';
import { saveListingData, clearListingData, getListingData, PendingListingData } from '../utils/listingStorage';
import { useAuth } from '../contexts/AuthContext';
import { slugify } from '../utils/slugify'; // <--- Added Slugify Import

// 1. DATA CONSTANTS
const VEHICLE_DATA: Record<string, string[]> = {
  'ALFA ROMEO': ['Mito', 'Giulia', 'Giulietta', 'Stelvio', 'Tonale'],
  'AUDI': ['A1', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'Q2', 'Q3', 'Q5', 'Q7', 'Q8'],
  'BAIC': ['Kenbo S2', 'Kenbo S3'],
  'BESTUNE': ['T77'],
  'BMW': ['Série 1', 'Série 2', 'Série 3', 'Série 4', 'Série 5', 'Série 7', 'X1', 'X2', 'X3', 'X4', 'X5', 'X6'],
  'BYD': ['Atto3', 'Dolphin', 'F3', 'King', 'Song', 'Tang'],
  'CHANGAN': ['Hunter', 'Star Truck', 'Star Van'],
  'CHERY': ['Arrizo 5', 'Arrizo 8', 'Tiggo 1', 'Tiggo 2', 'Tiggo 3', 'Tiggo 4', 'Tiggo 7', 'Tiggo 8', 'QQ'],
  'CHEVROLET': ['Equinox', 'Groove', 'Cruze', 'Sonic', 'Aveo', 'Spark', 'Captiva'],
  'CITROËN': ['Berlingo Multispace', 'Berlingo Van', 'C1', 'C3', 'C3 Picasso', 'C3 Air-cross', 'C-Elysée', 'C4', 'C4 X', 'C4 Cactus', 'C5', 'C5 Air-cross', 'C4 Picasso', 'C6', 'Jumpy Fourgon', 'Jumpy Combi', 'Jumper', 'Nemo', 'Nemo Multispace'],
  'CUPRA': ['Leon', 'Formentor', 'Terramar'],
  'DACIA': ['Logan', 'Sandero Stepway', 'Dokker', 'Sandero', 'Duster', 'Logan MCV'],
  'DFSK': ['Glory 580', 'K01H', 'K02S', 'K01S', 'Glory 500'],
  'DONGFENG': ['AX4', 'Box', 'Rich 6', 'Shine', 'S50', 'SX3', 'T5 EVO'],
  'DS': ['DS7', 'DS3', 'DS4', 'DS5'],
  'FAW': ['Besturn X40'],
  'FIAT': ['Doblo', 'Punto', 'Linea', '500', 'Ducato', 'Tipo', 'Panda', 'Qubo', 'Fiorino', 'Fiat 500 X'],
  'FODAY': ['F22 SC', 'F22 DC'],
  'FORD': ['Everest', 'KA', 'Ranger', 'Fiesta', 'Ecosport', 'Focus', 'Transit', 'Kuga', 'Fusion', 'Mustang'],
  'FOTON': ['Tunland G7 SC', 'Tunland G7 DC'],
  'GAC': ['GA4', 'GS3', 'GS4', 'EMKOO', 'EMZOOM'],
  'GEELY': ['COOLRAY', 'GX3', 'GC6', 'Azkarra'],
  'GMW': ['Poer', 'M4', 'C30', 'H5', 'Wingle 6', 'Wingle 5', 'Jolion', 'Haval H2', 'Haval H6', 'Haval H8', 'Haval H9'],
  'HONDA': ['Amaze', 'Civic', 'Brio', 'Jazz', 'City', 'HR-V', 'Accord', 'CR-V', 'ZR-V'],
  'HUANGHAI': ['N3'],
  'HYUNDAI': ['Tucson', 'Kona', 'Venue', 'Bayon', 'Ioniq 5', 'Palisade', 'Staria', 'Grand i10', 'Grand i10 Sedan', 'i20', 'Accent', 'i30', 'Elantra', 'Veloster', 'IX 35', 'H1', 'Santa Fe', 'Creta'],
  'ISUZU': ['D-Max SC', 'D-Max DC'],
  'IVECO': ['Daily SC', 'Daily DC'],
  'JAC': ['JAC T8 Pro DC'],
  'JAGUAR': ['E-PACE', 'XF', 'XJ', 'F-Type', 'XE', 'F-PACE'],
  'JEEP': ['Compass', 'Wrangler', 'Cherokee', 'Renegade', 'Grand Cherokee'],
  'JETOUR': ['Dashing', 'X70 Plus', 'T2'],
  'JMC': ['Vigus SC', 'Vigus DC'],
  'KIA': ['Seltos', 'Xceed', 'Niro', 'Soul', 'Stonic', 'EV6', 'EV9', 'Rio', 'Cerato', 'Optima', 'Carens', 'K 2500', 'Sorento', 'Quoris', 'Sportage', 'Picanto', "Cee'd", 'K2700'],
  'LADA': ['Urban'],
  'LANCIA': ['Ypsilon'],
  'LAND ROVER': ['Range Rover Sport', 'Range Rover Velar', 'Discovery Sport', 'Defender', 'Range Rover Evoque'],
  'MAHINDRA': ['XUV 300', 'XUV 500', 'KUV 100', 'Pick-Up SC', 'Pick-Up DC'],
  'MAZDA': ['Mazda 2', 'Mazda 3', 'Mazda 6', 'Mazda CX5', 'Mazda CX7', 'Mazda CX9', 'Mazda BT-50'],
  'MERCEDES-BENZ': ['Classe A', 'Classe B', 'Classe C', 'Classe CL', 'Classe GL', 'Classe E', 'Classe G', 'Classe X', 'Sprinter', 'Vito', 'Citan'],
  'MG': ['MG 5', 'MG 6', 'MG HS', 'MG EHS', 'MG RX8', 'MG MARVEL R', 'MG RX5', 'MG ONE', 'MG E-ZS', 'MG 3 Hybride', 'MG 4', 'MG GT', 'MG 3', 'MG GS', 'MG ZS'],
  'MINI': ['3 portes', '5 portes', 'Countryman'],
  'MITSUBISHI': ['Eclipse Cross', 'L200', 'Lancer', 'Mirage', 'Attrage', 'ASX', 'Pajero'],
  'NISSAN': ['Micra', 'Sunny', 'Juke', 'X-trail', 'Qashqai', 'Navara'],
  'OPEL': ['Adam', 'Crossland', 'Mokka', 'Combo Cargo', 'Corsa', 'Insignia', 'Astra', 'Meriva', 'Zafira'],
  'PEUGEOT': ['107', '108', 'Landtrek SC', 'Landtrek dC', 'Rifter', 'Bipper', 'Partner', '206', '206 Plus', '207', '208', 'Expert', 'Expert Combi', '301', '308', '407', '508', '1007', '2008', '3008', '4008', '5008', 'Boxer', 'RCZ', 'Traveller'],
  'PORSCHE': ['Taycan', 'Boxster', 'Cayman', 'Cayenne', '911', 'Panamera', 'Macan'],
  'RENAULT': ['Arkana', 'Express Van', 'Espace', 'Kwid', 'Master', 'Austral', 'Symbol', 'Clio', 'Captur', 'Talisman', 'Fluence', 'Koleos', 'Trafic', 'Twingo', 'Megane', 'Megane Sedan', 'Scénic', 'Laguna', 'Kadjar'],
  'SEAT': ['Ateca', 'Tarraco', 'Ibiza', 'Leon', 'Altea', 'Toledo', 'Arona', 'Mii'],
  'SKODA': ['Scala', 'Kamiq', 'Karoq', 'Superb', 'Kushaq', 'Kodiaq', 'Fabia', 'Rapid', 'Octavia', 'Yeti'],
  'SOUEAST': ['DX3', 'DX7'],
  'SSANGYONG': ['Musso', 'Tivoli', 'Korando', 'Actyon Sports', 'Rexton'],
  'SUZUKI': ['Ertiga', 'S-Presso', 'Jimny', 'Dzire', 'Vitara', 'Swift', 'Baleno', 'Ciaz', 'Celerio'],
  'TATA': ['Xenon X2 SC', 'Xenon X2 DC', 'Super Ace SC'],
  'TOYOTA': ['Agya', 'Aygo', 'Auris', 'C-HR', 'Yaris Cross', 'Yaris Sedan', 'Prado', 'Prius', 'Land Cruiser', 'Hiace', 'Yaris', 'Avanza', 'Corolla', 'Corolla Sedan', 'Rav 4', 'Hilux'],
  'VOLKSWAGEN': ['Beetle', 'Tiguan', 'T-cross', 'T-roc', 'Virtus', 'Touran', 'Transporter', 'Crafter', 'Caddy', 'Golf 6', 'Golf 7', 'Golf 8', 'Sharan', 'Jetta', 'Amarok', 'Up', 'Passat', 'Polo', 'Polo Sedan', 'Scirocco'],
  'VOLVO': ['S60', 'S90', 'XC40', 'XC60', 'XC90', 'EX30', 'EX90'],
  'WALLYS': ['Wallys 719', 'Wallys 619', 'Wallys IRIS', 'Wallys 216', 'Wolf'],
  'ZXAUTO': ['Grand Tiger', 'Terra Lord'],
};

const BRANDS = Object.keys(VEHICLE_DATA).sort();
const FUEL_TYPES = ['Essence', 'Diesel', 'Hybride', 'Électrique', 'GPL'];
const TRANSMISSIONS = ['Manuelle', 'Automatique'];
const COLORS = ['Blanc', 'Noir', 'Gris', 'Argent', 'Bleu', 'Rouge', 'Marron', 'Vert', 'Beige', 'Jaune', 'Orange', 'Autre'];
const CONDITIONS = ['Nouveau', 'Excellent', 'Très bon', 'Bon', 'Correct', 'Endommagé/Pour pièces'];
const LOCATIONS = ['Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan', 'Bizerte', 'Béja', 'Jendouba', 'Kef', 'Siliana', 'Kairouan', 'Kasserine', 'Sidi Bouzid', 'Sousse', 'Monastir', 'Mahdia', 'Sfax', 'Gafsa', 'Tozeur', 'Kebili', 'Gabès', 'Medenine', 'Tataouine'];

// THE FULL LIST OF OPTIONS
const VEHICLE_FEATURES = [
  'ABS', 'Accès mains libres', 'Airbags', 'Alarme antivol', 'Allumage automatique des feux',
  'Android Auto', 'Anti-démarrage électronique', 'Anti-patinage (ESP)', 'Apple CarPlay',
  'Assistance au freinage (AFU)', 'Bluetooth', 'Bouton Start & Stop', 'Caméra de recul',
  'Chargeur de téléphone sans fil', 'Clé intelligente', 'Climatisation', 'Climatisation automatique',
  'Contrôle de pression des pneus', 'Démarrage mains libres', 'Démarrage sans clé',
  'Détecteur de pluie', 'Détecteur de stationnement', 'Direction assistée', 'Fermeture centralisée à distance',
  'Feux à LED', 'Fixations ISOFIX', 'Frein de stationnement électrique', 'GPS', 'Jantes alliage',
  'Lave-phares', 'Limiteur de vitesse', 'Lumières d\'ambiance LED', 'Lèves-vitres électriques',
  'Ordinateur de bord', 'Ouverture coffre motorisée', 'Pare-brise athermique', 'Phares Full LED',
  'Phares LED', 'Radar de recul', 'Radio CD', 'Régulateur de vitesse', 'Rétroviseurs électriques',
  'Rétroviseurs rabattables', 'Sellerie cuir', 'Seuils de portes aluminium', 'Sièges chauffants',
  'Sièges réglables électriques', 'Système Park Assist', 'Système Start & Stop', 'Système de caméras 360',
  'Toit ouvrant', 'Toit panoramique en verre', 'Verrouillage centralisé', 'Vitres électriques',
  'Volant en cuir multifonctions', 'Volant réglable'
].sort();

interface FormStep {
  id: number;
  title: string;
  description: string;
  icon: any;
}

const FORM_STEPS: FormStep[] = [
  { id: 1, title: 'Véhicule', description: 'Identification', icon: Camera },
  { id: 2, title: 'Détails', description: 'Caractéristiques', icon: Star },
  { id: 3, title: 'Prix', description: 'Offre & Lieu', icon: MapPin },
  { id: 4, title: 'Médias', description: 'Photos & Info', icon: Upload },
  { id: 5, title: 'Contact', description: 'Coordonnées', icon: Phone }
];

const INPUT_STYLE = "block w-full rounded-lg bg-gray-50 border border-gray-300 text-gray-900 shadow-sm focus:bg-white focus:border-[#D4452C] focus:ring-[#D4452C] py-3 px-4 transition-colors duration-200";

const StepIndicator = ({ currentStep }: { currentStep: number }) => (
  <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-6">
    <h3 className="text-lg font-bold text-gray-900 mb-6">Progression</h3>
    <div className="space-y-0">
      {FORM_STEPS.map((step, index) => {
        const isCompleted = step.id < currentStep;
        const isActive = step.id === currentStep;
        return (
          <div key={step.id} className="relative flex items-start group">
            {index !== FORM_STEPS.length - 1 && (
              <div className={`absolute left-3.5 top-8 bottom-0 w-0.5 h-8 transition-colors duration-500 ease-in-out ${isCompleted ? 'bg-green-500' : 'bg-gray-100'}`} />
            )}
            <div className="flex items-center mr-4 mb-6 relative z-10">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 ${isCompleted ? 'bg-green-500 border-green-500 text-white shadow-sm' : isActive ? 'bg-[#D4452C] border-[#D4452C] text-white ring-2 ring-offset-2 ring-[#D4452C]/20' : 'bg-white border-gray-200 text-gray-300'}`}>
                {isCompleted ? <Check className="w-4 h-4" /> : <span className="text-xs font-bold">{step.id}</span>}
              </div>
            </div>
            <div className={`pt-1 transition-all duration-300 ${isActive || isCompleted ? 'opacity-100' : 'opacity-50'}`}>
              <p className={`text-sm font-bold transition-colors duration-300 ${isCompleted ? 'text-green-600' : isActive ? 'text-gray-900' : 'text-gray-500'}`}>{step.title}</p>
              <p className="text-xs text-gray-400">{step.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const MobileStepper = ({ currentStep }: { currentStep: number }) => (
  <div className="lg:hidden mb-6">
    <div className="flex justify-between items-center mb-2">
      <span className="text-sm font-bold text-gray-700">Étape {currentStep} sur 5</span>
      <span className="text-sm text-gray-500">{Math.round((currentStep / 5) * 100)}%</span>
    </div>
    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#D4452C] transition-all duration-500 ease-out" style={{ width: `${(currentStep / 5) * 100}%` }} />
    </div>
  </div>
);

export function SubmitListingPage() {
  const { user } = useAuth();
  const { navigate } = useRouter();
  const savedData = getListingData();

  const [formData, setFormData] = useState({
    brand: savedData?.brand || '',
    model: savedData?.model || '',
    year: savedData?.year || '',
    mileage: savedData?.mileage || '',
    fuelType: savedData?.fuelType || '',
    transmission: savedData?.transmission || '',
    color: savedData?.color || '',
    price: savedData?.price || '',
    description: savedData?.description || '',
    location: savedData?.location || '',
    condition: savedData?.condition || '',
    contactPhone: savedData?.contactPhone || '',
    contactEmail: savedData?.contactEmail || '',
    features: savedData?.features || [],
    negotiable: savedData?.negotiable ?? true,
  });

  const [imageUrls, setImageUrls] = useState<string[]>(savedData?.imageUrls || []);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    trackPageView('/submit');
    setLoading(false);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      const dataToSave: PendingListingData = { ...formData, imageUrls: imageUrls };
      saveListingData(dataToSave);
    }, 1000);
    return () => clearTimeout(handler);
  }, [formData, imageUrls]);

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'brand') setFormData(prev => ({ ...prev, model: '' }));
  };

  const toggleFeature = (feature: string) => {
    const newFeatures = formData.features.includes(feature)
      ? formData.features.filter(f => f !== feature)
      : [...formData.features, feature];
    handleInputChange('features', newFeatures);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    switch (step) {
      case 1:
        if (!formData.brand) newErrors.brand = 'Marque requise';
        if (!formData.model) newErrors.model = 'Modèle requis';
        if (!formData.year) newErrors.year = 'Année requise';
        if (!formData.mileage) newErrors.mileage = 'Kilométrage requis';
        break;
      case 2:
        if (!formData.fuelType) newErrors.fuelType = 'Type de carburant requis';
        if (!formData.transmission) newErrors.transmission = 'Transmission requise';
        if (!formData.color) newErrors.color = 'Couleur requise';
        if (!formData.condition) newErrors.condition = 'État requis';
        break;
      case 3:
        if (!formData.price) newErrors.price = 'Prix requis';
        if (!formData.location) newErrors.location = 'Localisation requise';
        break;
      case 4:
        if (!formData.description.trim()) newErrors.description = 'Description requise';
        if (formData.description.length < 10) newErrors.description = 'Minimum 10 caractères';
        break;
      case 5:
        if (!formData.contactPhone) newErrors.contactPhone = 'Téléphone requis';
        if (!formData.contactEmail) newErrors.contactEmail = 'Email requis';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) newErrors.contactEmail = 'Format email invalide';
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, FORM_STEPS.length));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(5)) return;
    if (!user) {
      const dataToSave: PendingListingData = { ...formData, imageUrls: imageUrls };
      saveListingData(dataToSave);
      navigate('/connecter?redirect=/soumettre-annonce');
      return;
    }
    setLoading(true);
    try {
      const userId = user.id;
      const validImageUrls = imageUrls.filter(url => url.trim() !== '');
      
      // GENERATE SLUG
      const baseSlug = slugify(`${formData.brand} ${formData.model} ${formData.year}`);
      const uniqueSuffix = Math.random().toString(36).substring(2, 7);
      const slug = `${baseSlug}-${uniqueSuffix}`;

      const { error: profileError } = await supabase.from('user_profiles').upsert({ user_id: userId }, { onConflict: 'user_id' });
      if (profileError) throw new Error(`Could not create your user profile. Please try again.`);

      const { error: listingError } = await supabase.from('listings').insert([{
        user_id: userId,
        brand: formData.brand,
        model: formData.model,
        year: parseInt(formData.year),
        mileage: parseInt(formData.mileage),
        fuel_type: formData.fuelType,
        transmission: formData.transmission,
        color: formData.color,
        price: parseFloat(formData.price),
        description: formData.description,
        location: formData.location,
        condition: formData.condition,
        contact_phone: formData.contactPhone,
        contact_email: formData.contactEmail,
        images: validImageUrls,
        features: formData.features,
        slug: slug, // <--- Added SLUG here
        status: 'pending'
      }]);

      if (listingError) throw new Error(`Failed to submit listing: ${listingError.message}`);
      clearListingData();
      setSuccess(true);
      setTimeout(() => navigate('/mes-annonces'), 4000);
    } catch (error: any) {
      console.error('Error submitting listing:', error);
      setErrors({ submit: error?.message || 'An unknown error occurred' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Annonce soumise !</h2>
          <p className="text-gray-500 mb-8">Votre annonce est en cours de modération. Vous serez redirigé vers votre tableau de bord dans quelques secondes.</p>
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-green-500 h-full w-full animate-[width_4s_linear]" style={{ width: '0%', animation: 'progress 4s linear forwards' }}></div>
          </div>
        </div>
        <style>{`@keyframes progress { from { width: 0%; } to { width: 100%; } }`}</style>
      </div>
    );
  }

  const currentStepData = FORM_STEPS[currentStep - 1];

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-sans pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-gray-900">Publier une annonce</h1>
          <p className="text-sm text-gray-500 mt-1">Remplissez le formulaire ci-dessous pour mettre votre véhicule en vente</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <MobileStepper currentStep={currentStep} />
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-100 p-6 bg-gray-50/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center">
                    <span className="bg-[#D4452C] text-white w-6 h-6 rounded flex items-center justify-center text-xs mr-3">{currentStep}</span>
                    {currentStepData.title}
                  </h2>
                </div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">{currentStepData.description}</div>
              </div>

              <div className="p-6 lg:p-8">
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Marque</label>
                        <select value={formData.brand} onChange={(e) => handleInputChange('brand', e.target.value)} className={`${INPUT_STYLE} ${errors.brand ? 'border-red-300 ring-red-200' : ''}`}>
                          <option value="">Sélectionner</option>
                          {BRANDS.map(brand => <option key={brand} value={brand}>{brand}</option>)}
                        </select>
                        {errors.brand && <p className="text-red-500 text-xs mt-1">{errors.brand}</p>}
                      </div>
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Modèle</label>
                        <select value={formData.model} onChange={(e) => handleInputChange('model', e.target.value)} disabled={!formData.brand} className={`${INPUT_STYLE} disabled:bg-gray-100 disabled:text-gray-400`}>
                          <option value="">{formData.brand ? 'Sélectionner' : 'Choisir une marque d\'abord'}</option>
                          {formData.brand && VEHICLE_DATA[formData.brand]?.map(model => (<option key={model} value={model}>{model}</option>))}
                        </select>
                        {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
                      </div>
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Année de mise en circulation</label>
                        <input type="number" placeholder="Ex: 2019" value={formData.year} onChange={(e) => handleInputChange('year', e.target.value)} className={`${INPUT_STYLE} ${errors.year ? 'border-red-300' : ''}`} />
                        {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
                      </div>
                      <div className="form-group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Kilométrage (km)</label>
                        <input type="number" placeholder="Ex: 85000" value={formData.mileage} onChange={(e) => handleInputChange('mileage', e.target.value)} className={`${INPUT_STYLE} ${errors.mileage ? 'border-red-300' : ''}`} />
                        {errors.mileage && <p className="text-red-500 text-xs mt-1">{errors.mileage}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Carburant</label>
                        <select value={formData.fuelType} onChange={(e) => handleInputChange('fuelType', e.target.value)} className={INPUT_STYLE}>
                          <option value="">Sélectionner</option>
                          {FUEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {errors.fuelType && <p className="text-red-500 text-xs mt-1">{errors.fuelType}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Transmission</label>
                        <select value={formData.transmission} onChange={(e) => handleInputChange('transmission', e.target.value)} className={INPUT_STYLE}>
                          <option value="">Sélectionner</option>
                          {TRANSMISSIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {errors.transmission && <p className="text-red-500 text-xs mt-1">{errors.transmission}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Couleur</label>
                        <select value={formData.color} onChange={(e) => handleInputChange('color', e.target.value)} className={INPUT_STYLE}>
                          <option value="">Sélectionner</option>
                          {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {errors.color && <p className="text-red-500 text-xs mt-1">{errors.color}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">État</label>
                        <select value={formData.condition} onChange={(e) => handleInputChange('condition', e.target.value)} className={INPUT_STYLE}>
                          <option value="">Sélectionner</option>
                          {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {errors.condition && <p className="text-red-500 text-xs mt-1">{errors.condition}</p>}
                      </div>
                    </div>
                    
                    {/* NEW OPTIONS SELECTOR */}
                    <div className="border-t border-gray-100 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <label className="block text-sm font-semibold text-gray-900">Équipements et Options</label>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{formData.features.length} sélectionnés</span>
                      </div>
                      <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-4 max-h-80 overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {VEHICLE_FEATURES.map(feature => {
                            const isSelected = formData.features.includes(feature);
                            return (
                              <label key={feature} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all duration-200 ${isSelected ? 'bg-white border-[#D4452C] shadow-sm ring-1 ring-[#D4452C]/10' : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
                                <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-[#D4452C] border-[#D4452C]' : 'bg-white border-gray-300'}`}>
                                  {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                </div>
                                <input type="checkbox" className="hidden" checked={isSelected} onChange={() => toggleFeature(feature)} />
                                <span className={`text-sm ${isSelected ? 'text-[#D4452C] font-medium' : 'text-gray-600'}`}>{feature}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Prix de vente</label>
                        <div className="relative rounded-md shadow-sm">
                          <input type="number" value={formData.price} onChange={(e) => handleInputChange('price', e.target.value)} className={`${INPUT_STYLE} pr-12 ${errors.price ? 'border-red-300' : ''}`} placeholder="0.00" />
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <span className="text-gray-500 sm:text-sm">TND</span>
                          </div>
                        </div>
                        {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                        <div className="mt-4 flex items-center">
                          <input id="negotiable" type="checkbox" checked={formData.negotiable} onChange={(e) => handleInputChange('negotiable', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#D4452C] focus:ring-[#D4452C]" />
                          <label htmlFor="negotiable" className="ml-2 block text-sm text-gray-700">Prix négociable</label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Ville / Gouvernorat</label>
                        <select value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} className={INPUT_STYLE}>
                          <option value="">Sélectionner</option>
                          {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start mt-6">
                      <Calculator className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-bold text-blue-900">Besoin d'aide pour le prix ?</h4>
                        <p className="text-sm text-blue-700 mt-1">Utilisez notre outil d'estimation IA pour obtenir la valeur marché de votre véhicule.<Link to="/estimateur" className="underline ml-1 font-semibold hover:text-blue-900">Estimer maintenant</Link></p>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 mb-3">Photos du véhicule</h3>
                      <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-6 transition-colors hover:border-gray-300">
                        <MultiImageUpload currentImages={imageUrls} onImagesUploaded={(urls) => setImageUrls(urls)} maxImages={10} />
                      </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                        Jusqu'à 10 photos - Formats JPG, PNG acceptés.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Description détaillée</label>
                      <textarea rows={6} value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} placeholder="Décrivez l'état général, l'historique d'entretien, les défauts éventuels..." className={`${INPUT_STYLE} ${errors.description ? 'border-red-300' : ''}`} />
                      <div className="flex justify-between items-center mt-2">
                        {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
                        <span className={`text-xs ml-auto ${formData.description.length < 10 ? 'text-orange-500' : 'text-gray-400'}`}>{formData.description.length} caractères (min. 10)</span>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-6">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
                        <div>
                          <h4 className="text-sm font-bold text-yellow-800">Presque fini !</h4>
                          <p className="text-sm text-yellow-700 mt-1">Vérifiez vos coordonnées. Ce sont ces informations que les acheteurs utiliseront pour vous joindre.</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Numéro de téléphone</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Phone className="h-4 w-4 text-gray-400" />
                          </div>
                          <input type="tel" value={formData.contactPhone} onChange={(e) => handleInputChange('contactPhone', e.target.value)} className={`${INPUT_STYLE} pl-10 ${errors.contactPhone ? 'border-red-300' : ''}`} placeholder="+216" />
                        </div>
                        {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse Email</label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Mail className="h-4 w-4 text-gray-400" />
                          </div>
                          <input type="email" value={formData.contactEmail} onChange={(e) => handleInputChange('contactEmail', e.target.value)} className={`${INPUT_STYLE} pl-10 ${errors.contactEmail ? 'border-red-300' : ''}`} placeholder="exemple@email.com" />
                        </div>
                        {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg mt-4">
                      <ShieldCheck className="h-4 w-4 text-green-600" />
                      <span>Vos données personnelles sont protégées.</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
                <button type="button" onClick={currentStep === 1 ? () => window.history.back() : prevStep} className="px-6 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition-all">{currentStep === 1 ? 'Annuler' : 'Retour'}</button>
                {currentStep < FORM_STEPS.length ? (
                  <button type="button" onClick={nextStep} className="inline-flex items-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-[#D4452C] hover:bg-[#b83a24] shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] transition-all">Suivant <ChevronRight className="ml-2 h-4 w-4" /></button>
                ) : (
                  <button type="submit" disabled={loading} className="inline-flex items-center px-8 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-[#D4452C] hover:bg-[#b83a24] shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] disabled:opacity-50 disabled:cursor-not-allowed transition-all">{loading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Traitement...</>) : ('Publier l\'annonce')}</button>
                )}
              </div>
            </form>
          </div>

          <div className="w-full lg:w-80 flex-shrink-0">
            <StepIndicator currentStep={currentStep} />
            <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-5 sticky top-[400px]">
              <div className="flex items-start"><Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2" /><h4 className="font-bold text-blue-900 text-sm">Conseil Pro</h4></div>
              <p className="text-sm text-blue-800 mt-2 leading-relaxed">
                {currentStep === 1 && "Les annonces avec des informations précises (Modèle exact, Année) reçoivent 40% de contacts en plus."}
                {currentStep === 2 && "N'oubliez pas de mentionner toutes les options. Le toit ouvrant et la boîte auto sont très recherchés."}
                {currentStep === 3 && "Un prix juste accélère la vente. Consultez les annonces similaires pour vous positionner."}
                {currentStep === 4 && "Prenez vos photos de jour, voiture propre. Ajoutez au moins une photo de l'intérieur."}
                {currentStep === 5 && "Vérifiez bien votre numéro. Les acheteurs appellent souvent dans l'heure qui suit la publication."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}